import {
  Select,
  TextField,
  DateRangePicker,
  DatepickerSimple,
  AVAILABLE_TIMEZONES,
  Checkbox,
  Timestamp,
  Timezone,
} from 'vopay';

import { Box, withStyles, type TransactionHistoryStatus } from 'vopay';
import { FC, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Transactions.module.scss';
import TransactionTypeLabel from './TransactionTypeLabel';
import { Balance } from '../hooks/useAccountBalances';

import { Button } from '#shared/components/UI';

const cn = withStyles(styles);

export type TransactionSearchParams = {
  type: string;
  status: TransactionHistoryStatus;
  flagged: string;
  currency: string;
  searchTerm: string;
  settlementDate: Timestamp | undefined;
  includeSubAccounts: boolean;
  timeRange: [Timestamp, Timestamp];
  timeZone: Timezone;
};

type SearchFormProps = {
  // Drives which transaction-type option set is offered.
  country: string;
  type: string;
  status: TransactionHistoryStatus;
  flagged: string;
  currency: string;
  searchTerm: string;
  timeZone: Timezone;
  balances: Balance[];
  initialTimeRange: [Timestamp, Timestamp];
  hasInvalidEndDate: boolean;
  shouldShowEndDateError: boolean;
  settlementDate: Timestamp | undefined;
  // The host computes the portal's provider gates (sub-account permissions);
  // the checkbox is visible-but-inert in the Activity tab, as on the portal.
  showIncludeSubAccounts: boolean;
  setShouldShowEndDateError: (event: boolean) => void;
  onSearch: (searchParams: TransactionSearchParams) => void;
};

// The portal's transaction filter bar, adapted for the contact drawer: the
// account facts arrive as props instead of portal providers.
const TransactionSearchForm: FC<SearchFormProps> = ({
  country,
  type,
  status,
  flagged,
  currency,
  searchTerm,
  initialTimeRange,
  hasInvalidEndDate,
  shouldShowEndDateError,
  timeZone,
  settlementDate,
  showIncludeSubAccounts,
  balances,
  setShouldShowEndDateError,
  onSearch,
}) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Shared.Transaction.TransactionHistory.TransactionSearchForm',
  });

  const [currentType, setCurrentType] = useState<string>(type);
  const [currentStatus, setCurrentStatus] = useState<TransactionHistoryStatus>(status);
  const [currentFlagged, setCurrentFlagged] = useState<string>(flagged);
  const [currentCurrency, setCurrentCurrency] = useState<string>(currency);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>(searchTerm);
  const [timeRange, setTimeRange] = useState<[Timestamp, Timestamp]>(initialTimeRange);
  const [currentTimeZone, setCurrentTimeZone] = useState<Timezone>(timeZone);
  const [currentIncludeSubAccounts, setCurrentIncludeSubAccounts] = useState<boolean>(false);
  const [currentSettlementDate, setCurrentSettlementDate] = useState<Timestamp | undefined>(settlementDate);
  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const initialTimeZones = AVAILABLE_TIMEZONES.map((tz) => ({
    label: tz,
    value: tz,
  })).filter((tz) => !tz.value.toLowerCase().includes('utc') && !tz.value.includes(timezoneName));

  const timeZoneOptions = [
    {
      label: 'UTC',
      value: 'UTC',
    },
    {
      label: timezoneName,
      value: timezoneName,
    },
    ...initialTimeZones,
  ];

  const typeOptions: { [key: string]: string } = {
    All: t('TypeOptions.all', { defaultValue: 'All' }),
    'EFT Withdrawal': t('TypeOptions.eftWithdrawal', { defaultValue: 'EFT Withdrawal' }),
    'EFT Funding': t('TypeOptions.eftFunding', { defaultValue: 'EFT Funding' }),
    'VoPayInstant Withdraw': t('TypeOptions.voPayInstantWithdraw', { defaultValue: 'VoPay Instant™ - Send' }),
    Refund: t('TypeOptions.refund', { defaultValue: 'Refund' }),
    'Currency Conversion': t('TypeOptions.currencyConversion', { defaultValue: 'Currency Conversion' }),
    'CrossBorder Withdrawal': t('TypeOptions.crossBorderWithdrawal', { defaultValue: 'Global Send' }),
    'Interac Money Request': t('TypeOptions.interacMoneyRequest', {
      defaultValue: 'Interac e-Transfer® Request Money',
    }),
    'Interac Bulk Payout': t('TypeOptions.interacBulkPayout', { defaultValue: 'Interac e-Transfer® Send' }),
    'Inbound e-Transfer': t('TypeOptions.inboundETransfer', { defaultValue: 'Interac e-Transfer® - Inbound' }),
    'Visa Direct Push': t('TypeOptions.visaDirectPush', { defaultValue: 'Visa Direct Push' }),
    'Credit Card': t('TypeOptions.creditCard', { defaultValue: 'Credit Card Acquiring' }),
    'Google Pay': t('TypeOptions.googlePay', { defaultValue: 'Google Pay' }),
    'PayPal Withdrawal': t('TypeOptions.payPalWithdrawal', { defaultValue: 'PayPal - Payout' }),
    'Account Transfer': t('TypeOptions.accountTransfer', { defaultValue: 'Account Transfer' }),
    Reversal: t('TypeOptions.reversal', { defaultValue: 'Reversal' }),
    'Bill Pay': t('TypeOptions.billPay', { defaultValue: 'Bill Pay' }),
    'Auto Balance Transfer': t('TypeOptions.autoBalanceTransfer', { defaultValue: 'Auto Balance Transfer' }),
    'Virtual Account Deposit': t('TypeOptions.virtualAccountDeposit', { defaultValue: 'Virtual Account Deposit' }),
    'Virtual Account Funding': t('TypeOptions.virtualAccountFunding', { defaultValue: 'Virtual Account Funding' }),
    'Virtual Account Withdraw': t('TypeOptions.virtualAccountWithdraw', { defaultValue: 'Virtual Account Withdraw' }),
    'Virtual Account Wire': t('TypeOptions.virtualAccountWire', { defaultValue: 'Virtual Account Wire' }),
    'Other Funding': t('TypeOptions.otherFunding', { defaultValue: 'Other Funding' }),
    'Other Withdrawal': t('TypeOptions.otherWithdrawal', { defaultValue: 'Other Withdrawal' }),
  };

  const typeOptionsUS: { [key: string]: string } = {
    All: t('TypeOptions.all', { defaultValue: 'All' }),
    'ACH Withdrawal': t('TypeOptions.achWithdrawal', { defaultValue: 'ACH Withdrawal' }),
    'ACH Funding': t('TypeOptions.achFunding', { defaultValue: 'ACH Funding' }),
    'RTP Withdrawal': t('TypeOptions.rtpWithdrawal', { defaultValue: 'RTP Payout' }),
    'Fedwire Withdrawal': t('TypeOptions.fedwireWithdrawal', { defaultValue: 'Fedwire Payout' }),
    'Fedwire Funding': t('TypeOptions.fedwireFunding', { defaultValue: 'Fedwire Funding' }),
    Refund: t('TypeOptions.refund', { defaultValue: 'Refund' }),
    'Currency Conversion': t('TypeOptions.currencyConversion', { defaultValue: 'Currency Conversion' }),
    'Visa Direct Push': t('TypeOptions.visaDirectPush', { defaultValue: 'Visa Direct Push' }),
    'Credit Card': t('TypeOptions.creditCard', { defaultValue: 'Credit Card Acquiring' }),
    'Google Pay': t('TypeOptions.googlePay', { defaultValue: 'Google Pay' }),
    'PayPal Withdrawal': t('TypeOptions.payPalWithdrawal', { defaultValue: 'PayPal - Payout' }),
    'Venmo Withdrawal': t('TypeOptions.venmoWithdrawal', { defaultValue: 'Venmo - Payout' }),
    'Zelle Withdrawal': t('TypeOptions.zelleWithdrawal', { defaultValue: 'Zelle Withdrawal' }),
    'Account Transfer': t('TypeOptions.accountTransfer', { defaultValue: 'Account Transfer' }),
    Reversal: t('TypeOptions.reversal', { defaultValue: 'Reversal' }),
    'Auto Balance Transfer': t('TypeOptions.autoBalanceTransfer', { defaultValue: 'Auto Balance Transfer' }),
    'Virtual Account Deposit': t('TypeOptions.virtualAccountDeposit', { defaultValue: 'Virtual Account Deposit' }),
    'Virtual Account Funding': t('TypeOptions.virtualAccountFunding', { defaultValue: 'Virtual Account Funding' }),
    'Virtual Account Withdraw': t('TypeOptions.virtualAccountWithdraw', { defaultValue: 'Virtual Account Withdraw' }),
    'Virtual Account Wire': t('TypeOptions.virtualAccountWire', { defaultValue: 'Virtual Account Wire' }),
    'Other Funding': t('TypeOptions.otherFunding', { defaultValue: 'Other Funding' }),
    'Other Withdrawal': t('TypeOptions.otherWithdrawal', { defaultValue: 'Other Withdrawal' }),
  };

  const statusOptions: Record<Exclude<TransactionHistoryStatus, null>, string> = {
    All: t('StatusOptions.all', { defaultValue: 'All' }),
    requested: t('StatusOptions.requested', { defaultValue: 'Requested' }),
    pending: t('StatusOptions.pending', { defaultValue: 'Pending' }),
    'in progress': t('StatusOptions.inProgress', { defaultValue: 'In Progress' }),
    sent: t('StatusOptions.sent', { defaultValue: 'Sent' }),
    successful: t('StatusOptions.successful', { defaultValue: 'Successful' }),
    complete: t('StatusOptions.complete', { defaultValue: 'Complete' }),
    settled: t('StatusOptions.settled', { defaultValue: 'Settled' }),
    failed: t('StatusOptions.failed', { defaultValue: 'Failed' }),
    cancelled: t('StatusOptions.cancelled', { defaultValue: 'Cancelled' }),
    'cancellation requested': t('StatusOptions.cancellationRequested', { defaultValue: 'Cancellation Requested' }),
    'pending approval': t('StatusOptions.pendingApproval', { defaultValue: 'Pending Approval' }),
    waiting: t('StatusOptions.waiting', { defaultValue: 'Waiting' }),
  };

  const flaggedOptions = {
    All: t('FlaggedOptions.all', { defaultValue: 'All' }),
    Yes: t('FlaggedOptions.yes', { defaultValue: 'Yes' }),
    No: t('FlaggedOptions.no', { defaultValue: 'No' }),
  };

  const currencyOptions = useMemo(() => {
    const options: { [key: string]: string } = {
      All: t('CurrencyOptions.all', { defaultValue: 'All' }),
    };

    balances.forEach((balance) => {
      options[balance.Currency] = balance.Currency;
    });

    return options;
  }, [balances]);

  return (
    <Box flx="col" gapSize="16px">
      <div className={cn('firstRow')}>
        <DateRangePicker
          onChange={(range) => {
            setTimeRange(range); // Update local state for time range
          }}
          initialTime={timeRange}
          triggerVariant="outlineShadow"
          hasInvalidEndDate={hasInvalidEndDate}
          shouldShowEndDateError={shouldShowEndDateError}
          setShouldShowEndDateError={setShouldShowEndDateError}
        />

        <div className={cn('timezone')}>
          <Select
            version="2"
            placeholder={t('timeZone', { defaultValue: 'Time Zone' }) || undefined}
            options={timeZoneOptions}
            name="timeZone"
            value={currentTimeZone}
            onBlur={() => {}}
            filterable
            onChange={(e) => {
              setCurrentTimeZone(e.target.value as Timezone);
            }}
            sort={false}
          />
        </div>
      </div>

      <div className={cn('secondRow')}>
        <div className={cn('defaultWidth')}>
          <Select
            version="2"
            placeholder={t('currency', { defaultValue: 'Currency' }) || undefined}
            options={convertToOptions(currencyOptions)}
            name="currency"
            value={currentCurrency}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentCurrency(e.target.value);
            }}
            sort={false}
          />
        </div>

        <div className={cn('defaultWidth', 'type')}>
          <Select
            version="2"
            placeholder={t('type', { defaultValue: 'Type' }) || undefined}
            options={country === 'US' ? convertToOptions(typeOptionsUS) : convertToOptions(typeOptions)}
            name="type"
            value={currentType}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentType(e.target.value);
            }}
            // Display only - the option's value (the raw TransactionType) is what gets submitted.
            renderOption={(o, { Option, SelectedIcon, selected }) => (
              <Option>
                <TransactionTypeLabel transactionType={o.value ?? ''} />
                {selected && <SelectedIcon />}
              </Option>
            )}
            // The trigger renders the plain label by default, so it needs the same treatment or
            // the selected rail loses the wordmark the option list showed.
            renderSelectedValue={(_, { value, Placeholder }) =>
              value ? (
                // nowrap keeps the trigger one line high, which the default renderer does
                // via its own line clamp.
                <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                  <TransactionTypeLabel transactionType={value} />
                </span>
              ) : (
                <Placeholder />
              )
            }
            sort={false}
          />
        </div>

        <div className={cn('defaultWidth', 'status')}>
          <Select
            version="2"
            placeholder={t('status', { defaultValue: 'Status' }) || undefined}
            options={convertToOptions(statusOptions)}
            name="status"
            value={currentStatus || ''}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentStatus(e.target.value as TransactionHistoryStatus);
            }}
            sort={false}
          />
        </div>

        <div className={cn('defaultWidth')}>
          <Select
            version="2"
            placeholder={t('flagged', { defaultValue: 'Flagged' }) || undefined}
            options={convertToOptions(flaggedOptions)}
            name="flagged"
            value={currentFlagged}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentFlagged(e.target.value);
            }}
            sort={false}
          />
        </div>

        <div className={cn('defaultWidth', 'settlementDate')}>
          <DatepickerSimple
            variant="outlineShadow"
            value={(currentSettlementDate || '').toString()}
            selected={currentSettlementDate}
            format="timestamp"
            position="bottomLeft"
            onChange={(e) => {
              setCurrentSettlementDate(e.target.value ? JSON.parse(e.target.value) : undefined);
            }}
            onBlur={() => {}}
            name="settlementDate"
            placeholder={t('settlementDatePlaceholder', { defaultValue: 'Settlement Date' })}
            inputWidth="auto"
            className={cn('settlementDatePicker')}
            allowBlank
          />
        </div>

        <div className={cn('defaultWidth')}>
          <TextField
            key="TransactionSearchKeyword"
            name="TransactionSearchKeyword"
            placeholder={t('keyword', { defaultValue: 'Keyword' }) || ''}
            value={currentSearchTerm}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentSearchTerm(e.target.value);
            }}
            variant="outlineShadow"
          />
        </div>

        <Button
          id="search-transaction-history"
          size="small"
          colorScheme="primary"
          onClick={() => {
            onSearch({
              type: currentType,
              status: currentStatus,
              flagged: currentFlagged,
              currency: currentCurrency,
              searchTerm: currentSearchTerm,
              timeRange,
              timeZone: currentTimeZone,
              includeSubAccounts: currentIncludeSubAccounts,
              settlementDate: currentSettlementDate,
            });
          }}
        >
          {t('search', { defaultValue: 'Search' })}
        </Button>

        {showIncludeSubAccounts && (
          <Box width="40%" jc="fs" maxheight="38px" base="8" pb="sm" pt="sm">
            <Checkbox
              label={t('includeSubAccounts', { defaultValue: 'Include sub-account transactions' }) || ''}
              name={'IncludeSubAccount'}
              variant="border"
              value={currentIncludeSubAccounts}
              onBlur={() => {}}
              onChange={(e) => {
                setCurrentIncludeSubAccounts(e.target.checked);
              }}
              labelSize="16px"
              labelPosition="right"
              hasNoPadding
              disabled={false}
            />
          </Box>
        )}
      </div>
    </Box>
  );
};

// The map is keyed by the raw API value (TransactionType, status, currency) with the
// translated display text as its value, so the option takes its label from the value and
// its submitted value from the key. Matches convertToOptions in the vopay package.
function convertToOptions(options: { [key: string]: string }) {
  return Object.entries(options).map(([key, value]) => ({
    label: value,
    value: key,
  }));
}

export default TransactionSearchForm;
